import React from "react";
import { motion } from "framer-motion";
import { HOOK } from "@/content";
import BlurReveal from "@/components/BlurReveal";
import HeroTerminal from "@/components/HeroTerminal";

export default function Hook() {
  return (
    <section
      id="top"
      className="chapter relative md:min-h-[100vh] md:flex md:items-center"
      data-testid="chapter-hook"
    >
      {/* Mobile-only hero photo — fills the space the 3D artifact occupies on desktop */}
      <div className="md:hidden relative w-full" data-testid="hook-mobile-photo">
        <div className="aspect-[4/3] w-full overflow-hidden relative">
          <picture>
            <source
              type="image/webp"
              srcSet="/images/sam-quantumeye-sm.webp"
              media="(max-width: 768px)"
            />
            <source type="image/webp" srcSet="/images/sam-quantumeye.webp" />
            <img
              src="/images/sam-quantumeye.jpg"
              alt="Sam Erpik mid-pitch at the QuantumEye booth, Retail Tech Show 2025"
              className="w-full h-full object-cover"
              style={{ objectPosition: "32% 28%" }}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          {/* gentle fade into page */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-paper to-transparent pointer-events-none" />
          {/* tiny credit chip */}
          <div className="absolute left-5 bottom-5 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/95">
              Retail Tech '25 · QuantumEye
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 w-full pt-10 md:pt-0 pb-16 md:pb-0 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="label-mono mb-6"
            data-testid="hook-kicker"
          >
            {HOOK.kicker}
          </motion.div>

          {/* The {" "} between lines keeps the text readable as one sentence for screen
              readers, search and copy-paste (the spans are blocks, so it adds no visible gap). */}
          {/* Fluid size keeps each of the three lines unwrapped at every width (the longest,
              "I build AI that", fits while font-size ≤ ~15% of the column; these stay ~8% under). */}
          <h1
            className="font-serif text-[length:clamp(40px,12.3vw,76px)] md:text-[length:clamp(54px,7.2vw,88px)] leading-[1.0] tracking-[-0.04em] text-ink"
            data-testid="hook-headline"
          >
            <BlurReveal
              as="span"
              text={HOOK.headlineA}
              className="block"
              delay={0.2}
              stagger={0.06}
              duration={0.85}
            />{" "}
            <BlurReveal
              as="span"
              text={HOOK.headlineB}
              className="italic block text-mute"
              delay={0.55}
              stagger={0.06}
              duration={0.9}
            />{" "}
            <BlurReveal
              as="span"
              text={HOOK.headlineC}
              className="italic block text-mute"
              delay={0.72}
              stagger={0.06}
              duration={0.9}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.15 }}
            className="mt-7 text-[17px] md:text-[19px] text-mute max-w-[52ch] leading-[1.55]"
          >
            {HOOK.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <kbd
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-black/10 font-mono text-[10px] tracking-[0.22em] uppercase text-mute"
              data-testid="hook-palette-hint"
            >
              <span>Press</span>
              <span className="text-ink font-semibold">⌘ K</span>
              <span>to navigate</span>
            </kbd>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-mute hidden md:inline">
              · or click the terminal
            </span>
          </motion.div>
        </div>

        <div className="hidden md:flex md:col-span-5 items-center justify-center md:justify-end">
          <HeroTerminal />
        </div>
      </div>

      {/* Mobile-only scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="flex md:hidden justify-center pb-8 pt-2 z-10"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-1.5 h-1.5 rounded-full bg-ink/30"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.55 }}
        className="hidden md:flex absolute bottom-10 left-6 md:left-12 z-10 items-center gap-3"
        data-testid="scroll-hint"
      >
        <span className="label-mono">Scroll to explore</span>
        <div className="h-10 w-[1px] bg-ink/30 relative overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 w-full h-4 bg-ink"
            animate={{ y: [-16, 40, -16] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: [0.45, 0, 0.55, 1] }}
          />
        </div>
      </motion.div>
    </section>
  );
}
